import React, {useCallback, useEffect, useState} from 'react';
import {IconDelete, IconEdit, IconFilter, IconPlus, IconSpinner, IconX} from "../../../../components/icons";
import Modal from "../../../../components/common/Modal";
import EtatChocForm from "./EtatChocForm";
import {useFormik} from "formik";
import {createEtatChoc, deleteEtatChoc, getEtatChocs, updateEtatChoc} from "../../../../components/requests";
import {EtatChoc} from "../../../../components/models";
import Pagination from "../../components/Pagination";
import {toast} from "react-toastify";
import SwitchButton from "../../../../components/common/SwitchButton";
import {useAuth} from "../../../contexts/AuthContext";

interface EtatChocProps {
  id?: string;
  type?: string;
  libelle?: string;
}

type EtatChocErrors = Partial<Record<keyof EtatChocProps, string>>;

const validate = (values: EtatChocProps) => {
  const errors: EtatChocErrors = {};

  if (!values.type) {
    errors.type = 'Ce champ est requis';
  }

  if (!values.libelle) {
    errors.libelle = 'Ce champ est requis';
  }

  return errors;
}

const EtatChocPage = () => {
  // const navigate = useNavigate();
  const { canWrite } = useAuth()
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isListLoading, setIsListLoading] = useState<boolean>(false);
  const [switchDisplay, setSwitchDisplay] = useState<string>('Etat');
  const [displayModal, setDisplayModal] = useState<string>('');
  const [page, setPage] = useState<number>(1);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [etats, setEtats] = useState<EtatChoc[]>([]);
  const [chocs, setChocs] = useState<EtatChoc[]>([]);
  const [etatChocToRemove, setEtatChocToRemove] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Liste des etats et des chocs paginées par 25
  const getListEtatChocs = useCallback(() => {
    setIsListLoading(true);
    getEtatChocs(page, switchDisplay, searchQuery)
      .then((response) => {
        if (response.status === 200 && response.data !== undefined) {
          setEtats(response.data.data.filter(data => data.type === 'Etat'));
          setChocs(response.data.data.filter(data => data.type === 'Choc'));
          setTotalItems(response.data.totalItems);
        }
      })
      .catch((error) => console.error(error))
      .finally(() => {
        setIsLoading(false);
        setIsListLoading(false);
      });
  }, [page, switchDisplay, searchQuery]);

  // Méthode d'ajout d'un état / choc
  const addEtatChoc = (data: EtatChoc) => {
    setIsListLoading(true);
    createEtatChoc(data)
      .then((response) => {
        if (response.status === 201) {
          const setList = (switchDisplay === 'Choc' ? setChocs : setEtats);
          const listData = {
            id: response?.data?.id,
            type: response?.data?.type,
            libelle: response?.data?.libelle,
          };
          setList([listData, ...(switchDisplay === 'Choc' ? chocs : etats)]);
          toast.success(`${switchDisplay} ajouté avec succès`);
        }
      })
      .catch((error) => console.error(error))
      .finally(() => {
        setIsListLoading(false);
        setDisplayModal('');
        setSwitchDisplay(data.type);
      });
  };

  // Méthode de modification d'un état / choc
  const editEtatChoc = (id: string, data: EtatChoc) => {
    setIsListLoading(true);
    updateEtatChoc(Number(id), data)
      .then((response) => {
        if (response.status === 200) {
          const setList = switchDisplay === 'Choc' ? setChocs : setEtats;
          const listData = {
            id: response?.data?.id,
            type: response?.data?.type,
            libelle: response?.data?.libelle,
          };
          const updatedList = (switchDisplay === 'Choc') ?
            chocs.map(choc => choc.id === Number(id) ? listData : choc) :
            etats.map(etat => etat.id === Number(id) ? listData : etat);
          setList(updatedList);
          toast.success(`${switchDisplay} modifié avec succès`);
        }
      })
      .catch((error) => console.error(error))
      .finally(() => {
        setIsListLoading(false);
        setDisplayModal('');
        setSwitchDisplay(data.type);
      });
  };

  // Méthode de suppression d'un état / choc
  const removeEtatChoc = (id: number) => {
    setIsListLoading(true);
    deleteEtatChoc(id)
      .then((response) => {
        if (response.status === 204) {
          const list = switchDisplay === 'Choc' ? setChocs : setEtats;
          const updatedList = (switchDisplay === 'Choc') ?
            chocs.filter(choc => choc.id !== id) :
            etats.filter(etat => etat.id !== id);
          list(updatedList);
          toast.success(`${switchDisplay} supprimé avec succès`);
        }
      })
      .catch((error) => console.error(error))
      .finally(() => {
        setIsListLoading(false);
        setDisplayModal('');
      });
  }

  // Méthode de soumission du formulaire de création / modification
  const formik = useFormik({
    initialValues: {
      id: '',
      type: switchDisplay,
      libelle: '',
    },
    enableReinitialize: true,
    validate,
    onSubmit: (values) => {
      const payload = {
        type: values?.type?.trim(),
        libelle: values?.libelle?.toUpperCase()?.trim(),
      };

      if (values?.id) editEtatChoc(values?.id, payload);
      else addEtatChoc(payload);
      formik.resetForm();
    },
  });

  // Méthode de recherche par rapport à la liste
  const formikSearch = useFormik({
    initialValues: {
      search: '',
    },
    onSubmit: values => {
      setSearchQuery(values?.search?.trim());
    },
  });

  useEffect(() => {
    getListEtatChocs();
  }, [getListEtatChocs]);

  const handleUpdate = (data: EtatChoc) => {
    setDisplayModal('modification');
    formik.setValues({
      id: data?.id?.toString(),
      type: data?.type,
      libelle: data?.libelle,
    })
  };

  const handleRemove = (id: number) => {
    setDisplayModal('suppression');
    setEtatChocToRemove(id);
  };

  const closeModal = () => {
    formik.resetForm();
    setDisplayModal('');
    setEtatChocToRemove(null);
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen-fit">
        <IconSpinner className="animate-spin w-10 h-10"/>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen-fit p-4 animate-in fade-in duration-300">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h3 className="text-lg font-bold text-gray-900">Tous les {switchDisplay === 'Etat' ? 'états' : 'chocs'}</h3>
          <SwitchButton
            leftValue={'Etat'}
            rightValue={'Choc'}
            switchValue={switchDisplay}
            setSwitchValue={setSwitchDisplay}
          />
        </div>

        <div className="flex gap-4">
          {canWrite('Parametrage') &&
            <button
              onClick={() => setDisplayModal('creation')}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium flex items-center gap-2"
            >
              <IconPlus/>
              Créer un {switchDisplay === 'Etat' ? 'état' : 'choc'}
            </button>
          }
        </div>
      </div>

      <form onSubmit={formikSearch.handleSubmit}>
        <div className="flex justify-end mb-2">
          <div className="flex gap-4 w-full">
            <div className="flex-1">
              <label className="form-label">Recherche</label>
              <input
                name="search"
                type="text"
                className="w-full form-input"
                onChange={formikSearch.handleChange}
              />
            </div>
            <div className="flex items-end">
              <button
                type="submit"
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium flex items-center gap-2"
              >
                <IconFilter/>
                Filtrer
              </button>
            </div>
          </div>
        </div>
      </form>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
          <tr>
            <th className="px-5 py-3 text-left font-semibold">{switchDisplay}</th>
            {canWrite('Parametrage') &&
                <th className="px-5 py-3 text-left font-semibold w-[100px]">Action</th>
            }
          </tr>
          </thead>

          <tbody className="divide-y divide-gray-100">
          {isListLoading ? (
            <tr>
              <td colSpan={12}>
                <div className="flex items-center justify-center min-h-[300px]">
                  <IconSpinner className="animate-spin w-10 h-10"/>
                </div>
              </td>
            </tr>
          ) : ((switchDisplay === 'Etat' ? etats : chocs)?.length === 0) ? (
            <tr>
              <td colSpan={12}>
                <div className="flex items-center justify-center min-h-[300px]">
                  <p className="text-gray-500">Pas de résultat</p>
                </div>
              </td>
            </tr>
          ) : (switchDisplay === 'Etat' ? etats : chocs)?.map((etatChoc) => {
            return (
              <tr key={etatChoc?.id} className="hover:bg-gray-50 transition-colors">
                <td className="px-5 py-4">
                  <p className="font-medium text-gray-900 text-sm">
                    {etatChoc?.libelle}
                  </p>
                </td>
                {canWrite('Parametrage') &&
                  <td className="px-5 py-4">
                    <div className="flex justify-center items-center gap-1">
                      <button
                        onClick={() => handleUpdate(etatChoc)}
                        className={`w-7 h-7 rounded text-xs font-medium flex justify-center items-center bg-green-50 text-green-600 hover:bg-green-100`}
                      >
                        <IconEdit/>
                      </button>
                      <button
                        onClick={() => handleRemove(etatChoc?.id)}
                        className={`w-7 h-7 rounded text-xs font-medium flex justify-center items-center bg-red-50 text-red-600 hover:bg-red-100`}
                      >
                        <IconDelete/>
                      </button>
                    </div>
                  </td>
                }
              </tr>
            )
          })}
          </tbody>
        </table>
      </div>

      {(!isListLoading && totalItems > 25) && (
        <div className="pt-4">
          <Pagination totalItems={totalItems} step={25} page={page} setPage={setPage}/>
        </div>
      )}

      {(displayModal) && (
        <Modal openModal={displayModal}>
          <div className="section-header flex justify-between">
            <div className="flex items-center gap-3">
              <div
                className={`section-icon ${displayModal === 'creation' || displayModal === 'modification' ? ' bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}>
                {displayModal === 'creation' ? <IconPlus/> : displayModal === 'modification' ? <IconEdit/> : <IconDelete/>}
              </div>
              <span className="section-title">
                {displayModal === 'creation' ? 'Créer' : displayModal === 'modification' ? 'Modifier' : 'Supprimer'}
                {(switchDisplay === 'Etat') ? ' un état' : ' un choc'}
              </span>
            </div>
            <button onClick={() => closeModal()}>
              <IconX/>
            </button>
          </div>

          <form onSubmit={formik.handleSubmit}>
            {(displayModal === 'creation' || displayModal === 'modification') ? (
              <EtatChocForm formik={formik} activeToggle={switchDisplay === 'Etat'}/>
            ) : (
              <div className="section-body p-3 text-sm">
                <p className="text-gray-600">Êtes-vous sûr de vouloir supprimer {(switchDisplay) ? 'ce choc' : 'cet etat'} ?</p>
                <p className="text-red-600">Cette action est irréversible.</p>
              </div>
            )}

            <div className="section-footer flex justify-end">
              <button
                type="button"
                onClick={() => closeModal()}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium flex items-center gap-2"
              >
                Annuler
              </button>
              <button
                type={(displayModal === 'suppression') ? 'button' : 'submit'}
                onClick={() => (displayModal === 'suppression') ? removeEtatChoc(etatChocToRemove) : null}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium flex items-center gap-2">
                {isLoading ? ((displayModal === 'creation' || displayModal === 'modification') ? 'Validation...' : 'Suppression...') : 'Valider'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
};

export default EtatChocPage;